import { useEffect, useState } from 'react';

/**
 * "Agora", em ms desde a época, relido a cada `intervalMs` enquanto `enabled`.
 *
 * O estado mora em quem chama: só esse componente re-renderiza a cada tique, e o pai e os irmãos
 * ficam como estão. Por isso o hook vai no componente mais baixo que mostra o tempo (ex.: o
 * `AutoRefreshIndicator` dos Meus Estudos, #2004), nunca numa casca que desenha uma lista.
 *
 * - Desligado, não há timer nem render; o valor fica o do último tique.
 * - Ao ligar, relê o relógio na hora (sem esperar o 1º intervalo): uma idade parada não aparece
 *   errada por até um tique.
 * - Trocar o `resetKey` (ex.: a hora de uma resposta nova) relê o relógio na hora e realinha os
 *   tiques a partir dali: uma idade contada desde esse instante sobe exatamente 1 por intervalo.
 * - Desligar e desmontar param o timer.
 *
 * @param intervalMs - Intervalo entre as leituras, em ms.
 * @param enabled - Liga o relógio. Desligue quando não há o que contar.
 * @param resetKey - Opcional: quando muda, o relógio é relido e o intervalo recomeça.
 * @returns O `Date.now()` do último tique (ou da montagem).
 */
export default function useNow(intervalMs: number, enabled: boolean, resetKey?: unknown): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs, enabled, resetKey]);

  return now;
}
